import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import GlobalLayout from './GlobalLayout';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { getCollectionByHash } from '../services/apiService';
import { useExternalBackButton } from '../hooks/useExternalBackButton';
import { appConfig } from '../config';
import './CourseDetail.css';
import { useTextToSpeech } from '../hooks/useTextToSpeech';

// --- HELPER: Wählt den Inhalt basierend auf der aktuellen Sprache ---
const getLocalizedContent = (item, field, langCode) => {
  if (!item) return '';
  const code = langCode ? String(langCode).split('-')[0].toLowerCase() : 'de';
  const localizedField = `${field}_${code}`;
  
  // Prüfen ob das feld_lang (z.B. name_en) existiert und nicht leer/null ist
  if (item[localizedField] && String(item[localizedField]).trim() !== '') {
    return item[localizedField];
  }
  // Fallback auf Default Feld (z.B. name)
  return item[field] || '';
};

// --- HELPER: Bild-URLs reparieren ---
const getImageUrl = (url) => {
  if (!url) return '';
  let cleanUrl = url;
  // Fix für falsche Pfade aus alten DB-Einträgen
  if (cleanUrl.includes('app.hejvi.de/img/') && !cleanUrl.includes('/api/img/')) {
    cleanUrl = cleanUrl.replace('app.hejvi.de/img/', 'app.hejvi.de/api/img/');
  }
  return cleanUrl;
};


const formatTime = (seconds) => {
  if (!seconds && seconds !== 0) return '0:00';
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

const CourseDetail = () => {
  const { courseId, hashId } = useParams();
  const resolvedId = courseId ?? hashId;
  const navigate = useNavigate();
  const location = useLocation();
  const { toggleFavourites } = useAuth();
  
  // Sprache holen
  const { t, currentLanguage } = useLanguage();
  const { speak, stop, isSpeaking } = useTextToSpeech();
  // --- FIX: backNavigationUrl HIER DEFINIEREN (vor der Nutzung) ---
  const backNavigationUrl = useMemo(() => {
    const sourcePage = location?.state?.sourcePage;
    if (sourcePage) return sourcePage;
    return '/dashboard';
  }, [location?.state?.sourcePage]);
  
  const { handleBackClick: defaultBackClick } = useExternalBackButton(backNavigationUrl);

  const handleCustomBack = () => {
    if (location.state?.searchContext && location.state?.sourcePage) {
      navigate(location.state.sourcePage, { 
        state: { 
          searchContext: location.state.searchContext 
        } 
      });
    } else {
      defaultBackClick();
    }
  };
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [collection, setCollection] = useState(null);
  const [chapters, setChapters] = useState([]);
  const [lessons, setLessons] = useState([]);
  const [refetchKey, setRefetchKey] = useState(0);
  const touchStartX = useRef(null);
  const containerRef = useRef(null);

  // Wenn wir von ElementCreator zurückkehren, Daten neu laden
  useEffect(() => {
    if (location.state?.newElementAdded) {
      setRefetchKey(prev => prev + 1);
      // State bereinigen damit kein Loop entsteht
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [location.state?.newElementAdded]);

  useEffect(() => {

    // Hilfsfunktion direkt im Effect definieren, um Dependency-Loop zu vermeiden
    const augmentLessons = (inputLessons, fallbackThumbnail) => {
      try {
        const list = Array.isArray(inputLessons) ? [...inputLessons] : [];
        const lower = (s) => String(s || '').toLowerCase();
        const findByName = (needle) => list.find(l => lower(l?.name || l?.title).includes(lower(needle)));
        const hasAny = (predicate) => list.some(predicate);
  
        // Reuse response hashes from any existing yes/no quiz if available
        const anyYesNo = list.find(l => Number(l?.type) === 3 && Number(l?.challenge_type) === 0 && (l?.response_correct_hash || l?.response_incorrect_hash));
        const sharedCorrectHash = anyYesNo?.response_correct_hash || null;
        const sharedIncorrectHash = anyYesNo?.response_incorrect_hash || null;
  
        // 1) Tube press → QR challenge
        const tube = findByName('tube press');
        const qrAlready = hasAny(l => Number(l?.type) === 3 && Number(l?.challenge_type) === 2);
        if (tube && !qrAlready) {
          const qrLesson = {
            id: `qr-${tube.hash_id || tube.id || Math.random().toString(36).slice(2)}`,
            hash_id: `qr-${tube.hash_id || tube.id || ''}`,
            type: 3,
            challenge_type: 2,
            name: 'Find the angle grinder and scan the QR code!',
            url_thumbnail: tube.url_thumbnail || tube.thumbnail || fallbackThumbnail || '',
            correct_option: 'hejvi-qr-001',
            response_correct_hash: sharedCorrectHash,
            response_incorrect_hash: sharedIncorrectHash
          };
          const tubeIdx = list.findIndex(l => (l?.hash_id || l?.id) === (tube.hash_id || tube.id));
          list.splice(Math.max(0, tubeIdx + 1), 0, qrLesson);
        }
  
        // 2) Electrical saw → Multiple choice challenge
        const saw = findByName('electrical saw');
        const mcAlready = hasAny(l => Number(l?.type) === 3 && Number(l?.challenge_type) === 3);
        if (saw && !mcAlready) {
          // Build option thumbs from available lessons (fallbacks)
          const candidates = list.filter(l => l?.url_thumbnail).slice(0, 6);
          const optionThumb = (i) => candidates[i]?.url_thumbnail || fallbackThumbnail || '',
          mcLesson = {
            id: `mc-${saw.hash_id || saw.id || Math.random().toString(36).slice(2)}`,
            hash_id: `mc-${saw.hash_id || saw.id || ''}`,
            type: 3,
            challenge_type: 3,
            name: 'Which Tool Is The Drill? Click On The Right Photo!',
            url_thumbnail: saw.url_thumbnail || saw.thumbnail || fallbackThumbnail || '',
            number_of_options: 4,
            option1_thumb: optionThumb(0),
            option2_thumb: optionThumb(1),
            option3_thumb: optionThumb(2),
            option4_thumb: optionThumb(3),
            correct_option: '3',
            response_correct_hash: sharedCorrectHash,
            response_incorrect_hash: sharedIncorrectHash
          };
          const sawIdx = list.findIndex(l => (l?.hash_id || l?.id) === (saw.hash_id || saw.id));
          list.splice(Math.max(0, sawIdx + 1), 0, mcLesson);
        }
        return list;
      } catch (_) {
        return inputLessons;
      }
    };


    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        // 1. Optimistische Daten aus Navigation nutzen (für sofortige Anzeige)
        const optimistic = location?.state?.collectionData;
        if (optimistic) {
          setCollection(optimistic);
          const optimisticLessons = Array.isArray(optimistic.elements) ? optimistic.elements : [];
          const uniqMap = new Map();
          optimisticLessons.forEach((l) => {
            const key = l?.hash_id || `id-${l?.id}`;
            if (!uniqMap.has(key)) uniqMap.set(key, l);
          });
          const base = Array.from(uniqMap.values());
          setLessons(augmentLessons(base));
        }

        // 2. Frische Daten von API laden (mit allen Übersetzungsfeldern!)
        if (resolvedId && typeof resolvedId === 'string') {
          const res = await getCollectionByHash(resolvedId);
          const payload = res || {};
          const data = payload.data || {};
          
          setCollection(data || null);
          setChapters(Array.isArray(payload.chapters) ? payload.chapters : (Array.isArray(data.chapters) ? data.chapters : []));
          
          const fromElements = Array.isArray(data.elements) ? data.elements : [];
          const fromRelated = Array.isArray(payload.related) ? payload.related : [];
          const finalLessons = fromElements.length ? fromElements : fromRelated;
          
          const uniqMap2 = new Map();
          finalLessons.forEach((l) => {
            const key = l?.hash_id || `id-${l?.id}`;
            if (!uniqMap2.has(key)) uniqMap2.set(key, l);
          });
          const base2 = Array.from(uniqMap2.values());
          setLessons(augmentLessons(base2));
        } else if (!optimistic) {
          setCollection(null);
        }
      } catch (e) {
        if (!location?.state?.collectionData) {
          setError('Failed to load course');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [resolvedId, location?.state?.collectionData, refetchKey]);

  /*const totalDuration = useMemo(() => {
    if (!lessons.length) return collection?.duration || 0;
    return lessons.reduce((sum, l) => sum + (Number(l.duration) || 0), 0);
  }, [lessons, collection]);
  */
  const levelLabel = useMemo(() => {
    const level = Number(collection?.level_complexity);
    if (level === 1) return t('beginner') || 'Beginner';
    if (level === 2) return t('intermediate') || 'Intermediate';
    if (level === 3) return t('advanced') || 'Advanced';
    return level ? `${t('levelComplexity') || 'Level Complexity'} ${level}` : '';
  }, [collection, t]);

  const hasMandatory = useMemo(() => {
    return (chapters || []).some((c) => String(c.is_mandatory) === '1');
  }, [chapters]);

  /*
  const backNavigationUrl = useMemo(() => {
    const sourcePage = location?.state?.sourcePage;
    if (sourcePage) return sourcePage;
    return '/dashboard';
  }, [location?.state?.sourcePage]);
  */
  // Wrap in useCallback
  const getNextLesson = React.useCallback(() => {
    if (!Array.isArray(lessons) || lessons.length === 0) return null;
    const notCompleted = lessons.find((l) => Number(l.progress) < 100);
    return notCompleted || lessons[0];
  }, [lessons]);

  const handleHeroPlay = () => {
    const next = getNextLesson();
    if (next?.hash_id) {
      // Index basierend auf der nächsten Lektion finden
      const nextIndex = lessons.findIndex(l => l.hash_id === next.hash_id);
      
      const navState = { 
        collectionData: collection,
        sourcePage: location?.state?.sourcePage,
        searchContext: location?.state?.searchContext,
        lessons: lessons,
        startIndex: nextIndex >= 0 ? nextIndex : 0,
        playlistContext: {
          lessons: lessons,
          currentIndex: nextIndex >= 0 ? nextIndex : 0,
          collectionId: collection?.hash_id
        }
      };

      if (appConfig?.workflow?.directVideoPlay) {
        navigate(`/element-feed/${hashId}`, { 
          state: { 
            lessons: collection.elements, 
            startIndex: nextIndex, // <--- KORRIGIERT: idx durch nextIndex ersetzt
            collectionData: collection,
            sourcePage: location.pathname
          } 
        });
      } else {
        navigate(`/element/${hashId}`, { 
          state: { 
            elementData: next, // <--- KORRIGIERT: idx durch das 'next' Objekt ersetzt
            sourcePage: location.pathname,
            collectionData: collection 
          } 
        });
      }
    } else if (collection?.url_element) {
      window.open(collection.url_element, '_blank');
    }
  };

  // Swipe handling
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const onTouchStart = (e) => {
      try { touchStartX.current = e.changedTouches[0].clientX; } catch (_) { touchStartX.current = null; }
    };
    const onTouchEnd = (e) => {
      if (touchStartX.current == null) return;
      const endX = e.changedTouches[0].clientX;
      const deltaX = endX - touchStartX.current;
      const threshold = 60; 
      if (deltaX <= -threshold) {
        const next = getNextLesson();
        if (next?.hash_id) {
          // Logic for next lesson (same as handleHeroPlay)
          const nextIndex = lessons.findIndex(l => l.hash_id === next.hash_id);
          const navState = { 
            collectionData: collection,
            sourcePage: location?.state?.sourcePage,
            searchContext: location?.state?.searchContext, // <--- NEU: Durchreichen
            lessons: lessons,
            startIndex: nextIndex >= 0 ? nextIndex : 0
          };
          if (appConfig?.workflow?.directVideoPlay) {
            navigate(`/element-feed/${next.hash_id}`, { state: navState });
          } else {
            navigate(`/element/${next.hash_id}`, { state: { ...navState, elementData: next } });
          }
        }
      } else if (deltaX >= threshold) {
        navigate(backNavigationUrl);
      }
      touchStartX.current = null;
    };

    el.addEventListener('touchstart', onTouchStart, { passive: true });
    el.addEventListener('touchend', onTouchEnd, { passive: true });
    return () => {
      el.removeEventListener('touchstart', onTouchStart);
      el.removeEventListener('touchend', onTouchEnd);
    };
  }, [lessons, navigate, backNavigationUrl, collection, getNextLesson, location]);

  const handleToggleFavourite = async () => {
    if (!collection?.hash_id) return;
    try {
      const res = await toggleFavourites(collection.hash_id);
      const favVal = Number(res?.data?.favourite_value) || 0;
      setCollection((prev) => ({ ...(prev || {}), favourite_value: favVal }));
    } catch (e) {
      console.error('Toggle favourite failed', e);
    }
  };

  const handleAddVideo = () => {
    // Kurs-Hash für Rückkehr speichern
    sessionStorage.setItem('hejvi_return_to_course', resolvedId);
    navigate('/create-element', {
      state: {
        returnUrl: `/course/${resolvedId}`,
        courseHashId: resolvedId
      }
    });
  };

  if (loading) {
    return (
      <GlobalLayout title="" showBackButton onBackClick={handleCustomBack} showNavbar>
        <div className="loading-area">
          <div className="spinner" />
          <p>{t('loading') || 'Loading…'}</p>
        </div>
      </GlobalLayout>
    );
  }

  if (error || !collection) {
    return (
      <GlobalLayout title="" showBackButton onBackClick={handleCustomBack} showNavbar>
        <div className="error-area">
          <p>{error || (t('notFound') || 'Content not found.')}</p>
          <button onClick={() => navigate(backNavigationUrl)}>{t('back') || 'Back'}</button>
        </div>
      </GlobalLayout>
    );
  }

  // --- ÜBERSETZUNG ANWENDEN ---
  const localizedTitle = getLocalizedContent(collection, 'name', currentLanguage);
  const localizedDesc = getLocalizedContent(collection, 'description', currentLanguage);

  return (
    <GlobalLayout 
      title={localizedTitle || collection.name || ''} 
      showBackButton 
      onBackClick={handleCustomBack} // <--- HIER ÄNDERN
      backTo={backNavigationUrl}
      showNavbar={true}
    >
      {/* FIX: Padding oben hinzufügen, damit der Header den Inhalt nicht verdeckt */}
      <div className="course-detail-container" ref={containerRef} style={{ paddingTop: '80px' }}>
        <div className="course-scroll">
          <div className="hero">
            <div className="hero-media" style={{ position: 'relative' }}>
              {/* FIX: getImageUrl benutzen für korrekte Bild-URL */}
              <img
                src={getImageUrl(collection.url_thumbnail)}
                alt={collection.name}
              />
              <button className="hero-play" onClick={handleHeroPlay}>
                <svg width="72" height="72" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="12" fill="#FFFFFF" />
                  <path d="M9 7.5V16.5L17 12L9 7.5Z" fill="#FF6407" />
                </svg>
              </button>
              
              <div className="ef-progress" style={{
                position: 'absolute',
                bottom: '0px',
                left: '0px',
                right: '0px',
                zIndex: 10
              }}>
                <div 
                  className="ef-progress-fill"
                  style={{
                    width: `${Math.min(100, Math.max(0, Number(collection?.progress || 0)))}%`
                  }}
                />
              </div>
              
              <div style={{
                position: 'absolute',
                bottom: '8px',
                left: '12px',
                color: '#fff',
                fontSize: '12px',
                fontWeight: '600',
                textShadow: '0 2px 4px rgba(0,0,0,0.8), 0 1px 2px rgba(0,0,0,0.6)',
                zIndex: 11
              }}>
                {formatTime((collection?.progress || 0) / 100 * (collection?.duration || 0))} / {formatTime(collection?.duration || 0)}
              </div>
            </div>
          </div>

          <div className="course-header">
            {/* ÄNDERUNG: Titel-Bereich mit neuem Stern-Design (analog ElementPage) */}
            <div className="course-title-area" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <h2 style={{ margin: 0, flex: 1 }}>{localizedTitle || collection.name}</h2>
              
              <button
                type="button"
                onClick={handleToggleFavourite}
                aria-label={Number(collection.favourite_value) ? (t('unfavorite') || 'Unfavorite') : (t('favorite') || 'Favorite')}
                style={{ 
                    background: 'transparent', 
                    border: 'none', 
                    fontSize: '28px', 
                    lineHeight: 1,
                    color: Number(collection.favourite_value) ? '#FF6407' : '#D1D5DB', // Orange (aktiv) / Grau (inaktiv)
                    cursor: 'pointer',
                    padding: '0 0 0 12px'
                }}
              >
                ★
              </button>
            </div>
            <div className="meta-row">
              {Number(collection.duration) > 0 && (
                <span className="meta">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                    <path d="M12 7v6l4 2" stroke="#FF6407" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <circle cx="12" cy="12" r="9" stroke="#FF6407" strokeWidth="2" fill="none"/>
                  </svg>
                  {Number(collection.duration) < 60 
                      ? `${Number(collection.duration || 0)} ${t('sec') || 'sec'}` 
                      : `${formatTime(Number(collection.duration) || 0)} ${t('min') || 'min'}`  
                    }
                    
                </span>
              )}
              {levelLabel && (
                <span className="meta"><svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M8 17l4-10 4 10M5 17h14" stroke="#344054" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>{levelLabel}</span>
              )}
            </div>
                          {(hasMandatory) && (
                <div className="chips">
                  <span className="chip mandatory">{t('mandatory') || 'Mandatory'}</span>
                </div>
              )}
          </div>

          {(localizedDesc || (collection.description && collection.description.trim())) && (
            <div className="description-card">
              <p>{localizedDesc || collection.description}</p>
              {/* VORLESE BUTTON */}
                <button 
                  onClick={() => isSpeaking ? stop() : speak(localizedDesc, currentLanguage)}
                  style={{
                    background: 'rgba(255, 100, 7, 0.1)',
                    border: 'none',
                    borderRadius: '50%',
                    width: '40px',
                    height: '40px',
                    cursor: 'pointer',
                    fontSize: '18px'
                  }}
                  title="Beschreibung vorlesen"
                >
                  {isSpeaking ? '⏹️' : '🔊'}
                </button>
            </div>
          )}

          {collection.qr_direct_url && (
            <div className="qr-card">
              <div className="qr-card-title">{t('qrCode') || 'QR-Code'}</div>
              <div className="qr-card-content">
                <img
                  src={collection.qr_direct_url}
                  alt="QR-Code"
                  className="qr-card-img"
                />
                <button
                  className="qr-print-btn"
                  onClick={() => {
                    const title = localizedTitle || collection.name || '';
                    const printWindow = window.open('', '_blank');
                    printWindow.document.write(`
                      <html><head><title>${title}</title>
                      <style>
                        body { margin: 0; display: flex; flex-direction: column; align-items: center; justify-content: center; min-height: 100vh; font-family: sans-serif; }
                        img { width: 280px; height: 280px; }
                        h2 { margin-bottom: 16px; font-size: 18px; text-align: center; }
                      </style></head>
                      <body>
                        <h2>${title}</h2>
                        <img src="${collection.qr_direct_url}" alt="QR-Code" />
                      </body></html>
                    `);
                    printWindow.document.close();
                    printWindow.focus();
                    printWindow.print();
                    printWindow.close();
                  }}
                  aria-label={t('printQrCode') || 'QR-Code drucken'}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
                    <path d="M6 9V2h12v7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <rect x="6" y="14" width="12" height="8" rx="1" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  {t('printQrCode') || 'QR-Code drucken'}
                </button>
              </div>
            </div>
          )}

          <div className="lessons-card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <h3 style={{ margin: 0 }}>{t('lessons') || 'Lessons'}</h3>
              <button
                className="cd-add-video-btn"
                onClick={handleAddVideo}
                aria-label={t('addVideo') || 'Video hinzufügen'}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
                  <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                  <line x1="12" y1="8" x2="12" y2="16" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  <line x1="8" y1="12" x2="16" y2="12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
                {t('addVideo') || '+ Video'}
              </button>
            </div>
            {Array.isArray(lessons) && lessons.length > 0 ? (
              <div className="lessons-list">
                {lessons.map((l, idx) => {
                  // --- FIX: ÜBERSETZUNG LEKTION ---
                  const lessonName = getLocalizedContent(l, 'name', currentLanguage) || l.name;
                  const progressVal = parseFloat(l.progress || 0);
                  const isDone = progressVal >= 100;

                  return (
                  <button className="lesson-item" key={l.id || idx} onClick={() => {
                    const navState = { 
                      collectionData: collection,
                      sourcePage: location?.state?.sourcePage,
                      lessons: lessons,
                      startIndex: idx
                    };
                    if (appConfig?.workflow?.directVideoPlay) {
                      navigate(`/element-feed/${l.hash_id}`, { state: navState });
                    } else {
                      navigate(`/element/${l.hash_id}`, { state: { ...navState, elementData: l } });
                    }
                  }}>
                    {/* --- FIX: ICONS (SVG statt Text) --- */}
                    <div className="lesson-icon">
                      {isDone ? (
                        <div style={{ 
                            width: 24, height: 24, borderRadius: '50%', background: '#22c55e', 
                            display: 'flex', alignItems: 'center', justifyContent: 'center' 
                        }}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="20 6 9 17 4 12"></polyline>
                            </svg>
                        </div>
                      ) : (
                        <div style={{ 
                            width: 24, height: 24, borderRadius: '50%', background: '#F3F4F6', 
                            display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid #E5E7EB'
                        }}>
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="#6B7280">
                                <polygon points="5 3 19 12 5 21 5 3"></polygon>
                            </svg>
                        </div>
                      )}
                    </div>

                    <div className="lesson-info">
                      {/* ÜBERSETZTER NAME */}
                      <div className="lesson-title">{lessonName}</div>
                      <div className="lesson-progress">
                        <div className="lesson-progress-bar">
                          <div 
                            className="lesson-progress-fill" 
                            style={{ width: `${Math.min(100, Math.max(0, progressVal))}%` }}
                          />
                        </div>
                        <span className="lesson-progress-text">{Math.round(progressVal)}%</span>
                      </div>
                    </div>
                    <div className="lesson-duration">
                      {/* FIX: el.duration statt collection.duration nutzen */}
                      {Number(l.duration) < 60 
                        ? `${Number(l.duration || 0)} ${t('sec') || 'sec'}` 
                        : `${formatTime(Number(l.duration) || 0)} ${t('min') || 'min'}`  
                      }  
                    </div>
                  </button>
                  );
                })}
              </div>
                          ) : (
                <div className="error-area" style={{ padding: '16px', textAlign: 'center', color: '#666' }}>
                  <p>{t('contentNotFound') || 'Content not found'}</p>
                </div>
              )}
          </div>
        </div>
      </div>
    </GlobalLayout>
  );
};

export default CourseDetail;